import BoxWrapper from 'blocks/BoxWrapper';
import Card from 'blocks/Card';
import { Row, RowItem } from 'blocks/CommonLayout';
import BadgeText from 'components/BadgeText';
import Breadcrumb from 'components/Breadcrumb';
import Loading from 'components/Loading';
import { getAlphabetDate } from 'helper/date';
import Paths from 'helper/paths';
import WithSidebar from 'layouts/WithSidebar';
import BreadcrumbInterface from 'models/breadcrumb/breadcrumb.interface';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ajax from 'utils/ajax';
import urls from 'utils/urls';
import ChatBox from '../components/ChatBox';
import { TicketDetail, TicketInterfae } from '../ticket.interface';
import * as S from './singleTicket.styles';

const SingleTicket: React.FC = () => {
  const { t } = useTranslation();
  const breadcrumbLinks = [
    {
      title: t(`pageTitles.${Paths.Dashboard.index().name}`),
      url: Paths.Dashboard.index().route,
      active: true,
    },
    {
      title: t(`pageTitles.${Paths.Tickets.index().name}`),
      url: Paths.Tickets.index().route,
      active: true,
    },
    {
      title: t(`pageTitles.${Paths.Tickets.single().name}`),
      url: Paths.Tickets.single().route,
      active: false,
    },
  ] as BreadcrumbInterface[];

  const { ticketId } = useParams<any>();

  const [ticketData, setTicketData] = useState<TicketInterfae | null>(null);
  const [chatData, setChatData] = useState<Array<TicketDetail>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTicketDetail();
  }, []);

  const getTicketDetail = async () => {
    const res = await ajax<TicketInterfae>({
      url: urls.ticketDetail(ticketId || ''),
      method: 'GET',
    });

    if (!res) return;

    setTicketData(res);
    setChatData([
      {
        id: `ticket-${res.id}`,
        userId: res.userId,
        adminId: null,
        content: res.mainContent,
        createdAt: res.createdAt,
        ticketId: res.id,
        type: 'text',
        updatedAt: res.updatedAt,
      },
      ...res.TicketDetails,
    ]);
    setLoading(false);
  };

  const onSendMessage = async (text, file) => {
    const data = {
      content: text,
      file: file,
    };
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      return formData.append(key, data[key]);
    });
    const res = await ajax<TicketDetail>({
      url: urls.ticketSendMessage(ticketId || ''),
      method: 'POST',
      data: formData,
    });

    if (!res) return;

    setChatData([...chatData, res]);
  };

  const getBadgeColor = (status: string) => {
    if (status === 'new') return 'success';
    if (status === 'closed') return 'error';
    return 'warn';
  };

  if (loading || !ticketData) return <Loading />;

  const ticketDetail = [
    {
      ketItem: t('pages.tickets.single.info.subject'),
      valueItem: ticketData.subject,
    },
    {
      ketItem: t('pages.tickets.single.info.admin'),
      valueItem: ticketData.Admin ? ticketData.Admin.fname + ' ' + ticketData.Admin.lname : '---',
    },
    {
      ketItem: t('pages.tickets.single.info.date'),
      valueItem: getAlphabetDate(ticketData.createdAt),
    },
    {
      ketItem: t('pages.tickets.single.info.status'),
      valueItem: <BadgeText color={getBadgeColor(ticketData.status)}>{ticketData.status}</BadgeText>,
    },
  ];

  return (
    <WithSidebar>
      <S.Wrapper>
        <BoxWrapper>
          <Breadcrumb links={breadcrumbLinks} />
        </BoxWrapper>

        <BoxWrapper>
          <Card boxShadow="low" space="sm">
            <S.Row as={Row}>
              {ticketDetail.map((item, index) => {
                return (
                  <S.RowItem as={RowItem} key={index}>
                    <S.SingleTicketItemKey className="key">{item.ketItem}:</S.SingleTicketItemKey>
                    <S.SingleTicketItemValue className="value">{item.valueItem}</S.SingleTicketItemValue>
                  </S.RowItem>
                );
              })}
            </S.Row>
          </Card>
        </BoxWrapper>

        <S.SingleChatBoxWrapper as={BoxWrapper} className="chat-box-wrapper">
          <ChatBox onSend={onSendMessage} chatList={chatData} status={ticketData.status} />
        </S.SingleChatBoxWrapper>
      </S.Wrapper>
    </WithSidebar>
  );
};

export default SingleTicket;
